<template lang="pug">
  .scenario-popup-editor
    loading-overlay(:is-loading="saving")
    .card
      .card-header
        h3.card-title クリエイティブ設定
      .card-body.py-4.px-2
        .container-fluid.px-2.mt-10
          // エラーメッセージ
          .alert.alert-danger(v-if="hasError") 入力内容にエラーがあります。確認してください
          .alert.alert-danger(v-if="apiErrorMessages.length > 0")
            p.mb-0(v-for="msg in apiErrorMessages" :key="msg") {{ msg }}

          label 表示デバイス
          div.form-group.d-flex(v-if="!isChainedPopup")
            div.form-check
              input#show_both.form-check-input(
                type='radio',
                v-model='showDevice',
                @change='setShowDevice(showDevice)',
                name='showDevice',
                value='both',
                :disabled='edit_data && !is_copy'
              )
              label.form-check-label(for='show_both', :class="{'text-muted': edit_data && !is_copy}") PC・スマートフォン
            div.form-check.ml-3
              input#show_pc.form-check-input(
                type='radio',
                v-model='showDevice',
                @change='setShowDevice(showDevice)',
                name='showDevice',
                value='pc',
                :disabled='edit_data && !is_copy'
              )
              label.form-check-label(for='show_pc', :class="{'text-muted': edit_data && !is_copy}") PC
            div.form-check.ml-3
              input#show_sp.form-check-input(
                type='radio',
                v-model='showDevice',
                @change='setShowDevice(showDevice)',
                name='showDevice',
                value='sp',
                :disabled='edit_data && !is_copy'
              )
              label.form-check-label(for='show_sp', :class="{'text-muted': edit_data && !is_copy}") スマートフォン
          p.text-muted(v-else) 接続元のポップアップの設定を引き継ぎます
          div.form-group(v-if="showDevice == 'both' || showDevice == 'pc'")
            label(for='creative_max_width')
              | PC画像サイズ（横幅）
              help-tooltip(html-key='creativeMaxWidth')
            div
              select.form-control#creative_max_width(
                v-model="pcMaxWidth",
                @change="updatePcMaxWidth"
              )
                option(value='500') 500px（標準）
                option(value='600') 600px
                option(value='700') 700px
                option(value='800') 800px
                option(value='900') 900px
                option(value='1000') 1000px
                option(value='1100') 1100px
                option(value='1200') 1200px（最大）
                option(value='20%') 20%
                option(value='30%') 30%（標準）
                option(value='40%') 40%
                option(value='50%') 50%
                option(value='60%') 60%（最大）
          div.form-group(v-if="pop_up_animations != null && !isChainedPopup")
            label(for='creative_pop_up_animation') アニメーション種別
            select.form-control(@change="updatePopUpAnimation")
              option(v-for="(val, key) in pop_up_animations", :value="key") {{ val }}

          p.text-muted.text-xs.mb-0 ※PCの画像は横幅のサイズに合わせて、縦幅は同じ比率で拡大・縮小し表示されます。
          p.text-muted.text-xs ※スマートフォンの画像サイズの指定はできません。（デバイスの横幅に合わせて自動で拡大・縮小し表示されます。）

    .card
      .card-header
        h3.card-title 設問登録
      .card-body.py-4.px-2
        .container-fluid.px-2.mt-10
          .mb-3
            div
              label.form-label
                | 設問画像を追加({{ activeCollections.length }}/{{ maxScenarioImages }})
                help-tooltip(html-key='scenarioImageSize')
            span.custom-tooltip(:data-tooltip="getImageInputTooltip()")
              input(type="file", @change="handleImageUpload", accept=".jpg,.jpeg,.gif,.png,.webp", :disabled="isEditing || !canAddMoreImages", ref="questionImageInput")
            .form-text ファイルの選択後、ボタンの設定ができます

          // 編集エリア
          div(v-if="currentCollection && showEditor")
            .drag-drop-instruction.alert.alert-info.mb-3
              .d-flex.align-items-center
                i.bi.bi-cursor-fill.me-2
                div
                  strong ボタンの追加方法:
                  p.mb-0
                    template(v-if="canAddMoreChoices") ボタンに追加したい領域をドラッグ＆ドロップで囲んでください。
                    template(v-else) ボタンの上限に達しました。新しいボタンを追加するには、既存のボタンを削除してください。
            .position-relative.image-container(
              @mousedown="onStartDrawing",
              @mousemove="onUpdateDrawing",
              @mouseup="onStopDrawing",
              @mouseleave="onStopDrawing",
              :class="{ 'no-more-choices': !canAddMoreChoices }"
            )
              img.editor-image(:src="currentCollection.imageData", draggable="false")
              // 選択済みエリア
              div.selected-area(
                v-for="choice in currentCollection.choices",
                v-if="!choice._destroy",
                :key="choice.id",
                :style="{ left: choice.x + '%', top: choice.y + '%', width: choice.width + '%', height: choice.height + '%' }"
              )
                .choice-number {{ choice.buttonName }}
                .p-3
                  button.btn.btn-default.btn-xs(@click.stop="openAnimationModal(currentCollection.id || currentCollection.temp_id, choice.id || choice.temp_id)")
                    i.fas.mr-2(:class="choice.animationImagePreview ? 'fa-pencil-alt' : 'fa-plus'")
                    | {{ choice.animationImagePreview ? 'アニメーション編集' : 'アニメーション追加' }}
                  button.btn.btn-danger.btn-xs.ml-2(v-if="choice.animationImagePreview", @click.stop="deleteAnimationImage(currentCollection.id || currentCollection.temp_id, choice.id || choice.temp_id)")
                    i.fas.fa-trash-alt.mr-0
              // 現在選択中のエリア
              .drawing-area(
                v-if="isDrawing && canAddMoreChoices",
                :style="{ left: currentArea.x + '%', top: currentArea.y + '%', width: currentArea.width + '%', height: currentArea.height + '%' }"
              )
            .d-flex.justify-content-between.mt-2.mb-4
              div
                span.custom-tooltip(:data-tooltip="activeChoices(currentCollection).length === 0 ? 'ボタンを追加してください' : ''")
                  button.btn.btn-danger.mr-2(
                    @click="deleteAllChoices(currentCollection.id || currentCollection.temp_id)",
                    :disabled="activeChoices(currentCollection).length === 0"
                  ) ボタン全削除
                button.btn.btn-outline-secondary(
                  @click="showReplaceImageModal(currentCollection.id || currentCollection.temp_id)"
                ) 画像差替
              div
                span.custom-tooltip(:data-tooltip="activeChoices(currentCollection).length === 0 ? 'ボタンを追加してください' : ''")
                  button.btn.btn-primary(
                    @click="showEditor = false; $refs.questionImageInput && ($refs.questionImageInput.value = '')",
                    :disabled="activeChoices(currentCollection).length === 0"
                  ) 保存する
                button.btn.btn-secondary.ml-2(
                  @click="cancelCollectionEdit"
                ) キャンセル

          // ボタン全削除確認モーダル
          b-modal#delete-all-choices-modal(
            title="ボタン全削除の確認",
            size="md",
            hide-footer
          )
            p この設問のボタンをすべて削除しますか？
            p.text-muted.small ※パターンに設定されているこの設問のボタン選択は「*」にリセットされます。
            .d-flex.justify-content-end.mt-3
              b-button(variant="danger", @click="confirmDeleteAllChoices") 削除する
              b-button(variant="secondary", class="ml-2", @click="$bvModal.hide('delete-all-choices-modal')") キャンセル

          // 設問削除確認モーダル
          b-modal#delete-collection-modal(
            title="設問削除の確認",
            size="md",
            hide-footer
          )
            template(v-if="hasActivePatterns && !isLastCollection")
              p.text-danger 回答シナリオが登録されているため、この設問を削除できません。
              p 設問を削除するには、先に回答シナリオをすべて削除してください。
              .d-flex.justify-content-end.mt-3
                b-button(variant="secondary", @click="$bvModal.hide('delete-collection-modal')") 閉じる
            template(v-else)
              p この設問を削除しますか？
              p.text-muted.small ※この設問に設定されているボタンもすべて削除されます。
              .d-flex.justify-content-end.mt-3
                b-button(variant="danger", @click="confirmDeleteCollection") 削除する
                b-button(variant="secondary", class="ml-2", @click="$bvModal.hide('delete-collection-modal')") キャンセル

          // 設問画像差し替え確認モーダル
          b-modal#replace-image-modal(
            title="設問画像の差し替え",
            size="md",
            hide-footer,
            @hidden="resetReplaceImageModal"
          )
            template(v-if="!replaceImageFile")
              p 設問画像を差し替えます。
              p.text-danger ※画像の縦横比が異なる場合、ボタン位置がずれる可能性があります。
              p.text-muted.small 必要に応じてボタンを再設定してください。
              .form-group.mt-3
                label 新しい画像を選択
                input.form-control-file(
                  type="file",
                  @change="handleReplaceImageSelect",
                  accept=".jpg,.jpeg,.gif,.png,.webp",
                  ref="replaceImageInput"
                )
            template(v-else)
              p 以下の画像に差し替えます。よろしいですか？
              .text-center.my-3
                img.img-fluid(:src="replaceImagePreview", style="max-height: 200px;")
              .d-flex.justify-content-end.mt-3
                b-button(variant="primary", @click="confirmReplaceImage") 差し替える
                b-button(variant="secondary", class="ml-2", @click="cancelReplaceImage") キャンセル

          // アニメーション画像設定モーダル
          // ------------- アニメーション画像設定モーダル -------------
          b-modal#animation-image-modal(
            title="アニメーション画像設定",
            size="md",
            hide-footer,
            @hidden="resetAnimationModal"
          )
            div
              label.form-label(for="animationImageInput") アニメーション画像
              .mt-2
                input#animationImageInput(
                  type="file",
                  @change="handleAnimationImageUpload",
                  accept=".jpg,.jpeg,.gif,.png,.webp",
                  ref="animationImageInput"
                )
            div.mt-3(v-if="animationImagePreview")
              img.img-fluid(:src="animationImagePreview", alt="アニメーション画像プレビュー")
            div.mt-3.d-flex.justify-content-end
              b-button(variant="secondary", @click="$bvModal.hide('animation-image-modal')") キャンセル
              b-button(variant="primary", class="ml-2", @click="saveAnimationImage") 保存する
              b-button(variant="danger", class="ml-2", v-if="animationImagePreview", @click="deleteAnimationImageFromModal") 削除する

          // 画像コレクション一覧
          template(v-if="savedCollections.length > 0")
            h3.text-lg.text-bold 登録済み画像
            .question-grid
              .question-card(
                v-for="(collection, index) in savedCollections",
                :key="collection.id || collection.temp_id"
              )
                .q-title
                  span 設問 {{ index + 1 }}
                .image-wrapper
                  img.card-img-top(
                    :src="collection.imageData",
                    :alt="getCollectionName(index)"
                  )
                .q-meta
                  | {{ getCollectionName(index) }}
                  br
                  | ボタン数: {{ activeChoices(collection).length }}/5
                .q-actions
                  span.custom-tooltip.tooltip-top-center(:data-tooltip="editingTooltip()")
                    button.btn.btn-primary.btn-sm(
                      @click="selectCollection(collection)",
                      :disabled="isEditing",
                      :aria-label="'設問' + (index + 1) + 'を編集'"
                    ) 編集
                  span.custom-tooltip.tooltip-top-center(:data-tooltip="editingTooltip()")
                    button.btn.btn-danger.btn-sm(
                      @click="showDeleteCollectionModal(collection.id || collection.temp_id)",
                      :disabled="isEditing",
                      :aria-label="'設問' + (index + 1) + 'を削除'"
                    ) 削除
                .text-danger.mt-2.error-message(v-if="collectionErrors[index]") {{ collectionErrors[index] }}
          // ローディング画像（設問カードと区別した領域）
          .loading-image-block
            .form-check.mb-2
              input#show_loading_image.form-check-input(
                type='checkbox',
                v-model='showLoadingImage'
              )
              label.form-check-label(for='show_loading_image') ローディング画像を設定
              help-tooltip(html-key='scenarioLoadingImage')
            .loading-row(v-if="showLoadingImage")
              .loading-thumb(v-if="loadingImage")
                img(:src="loadingImage", alt="ローディング画像")
              .loading-thumb.loading-thumb--empty(v-else)
                span 未登録
              .loading-controls
                input(
                  type="file",
                  @change="handleLoadingImageUpload",
                  accept=".jpg,.jpeg,.gif,.png,.webp"
                )
                button.btn.btn-danger.btn-sm(
                  type="button",
                  @click="deleteLoadingImage",
                  v-if="loadingImage",
                  aria-label="ローディング画像を削除"
                ) 削除
    
    // 分岐回答設定
    .card(v-if="hasSavedImagesWithChoices")
      .card-header
        h3.card-title 結果画像の登録

      .card-body.py-4.px-2
        .container-fluid.px-2.scenario-result-section
          scenario-popup-simulator(
            :active-collections="activeCollections",
            :patterns="activePatterns",
            :default-pattern-object="defaultPatternObject",
            :chained-popup-options="chainedPopupOptions",
            @load-popup-options="preloadChainedPopupOptions"
          )
          
          // テーブル表示（左：追加ボタン／右：設問表）
          .scenario-list-row
            .scenario-list-side
              span.custom-tooltip(:data-tooltip="getPatternAddTooltip()")
                button.btn.btn-primary(
                  @click="openPatternEditor(null, -1)",
                  :disabled="isEditing || !hasImagesWithChoices || activePatterns.length >= maxPatterns"
                )
                  | シナリオを追加する
              .text-muted.small.mt-2
                p.mb-0 ※最大10通りのシナリオの登録が可能です。
            .scenario-wrap
              table.table.table-bordered.scenario-table
                thead
                  tr
                    th.text-center.col-priority 優先順
                    th.text-center.col-question(
                      v-for="(collection, idx) in questionSlots",
                      :key="idx",
                      :class="{ 'question-slot-empty': !collection }"
                    )
                      | 設問{{ idx + 1 }}
                    th.text-center.col-result 表示する回答
                    th.text-center.col-actions 編集/削除
                tbody
                  tr(
                    v-for="(pattern, index) in activePatterns",
                    :key="`pattern-${pattern.id || index}`"
                  )
                    td.text-center.align-middle
                      .order-controls
                        strong.priority-label {{ getPatternLabel(index) }}
                        .btn-group-vertical
                          span.custom-tooltip.tooltip-top-right(:data-tooltip="getPatternPriorityUpTooltip(index) || '上に移動'")
                            button.btn.btn-outline-secondary.btn-sm.priority-btn(
                              @click="movePatternUp(index)",
                              :disabled="isEditing || index === 0",
                              aria-label="上に移動"
                            )
                              i.fas.fa-chevron-up
                          span.custom-tooltip.tooltip-top-right(:data-tooltip="getPatternPriorityDownTooltip(index) || '下に移動'")
                            button.btn.btn-outline-secondary.btn-sm.priority-btn(
                              @click="movePatternDown(index)",
                              :disabled="isEditing || index === activePatterns.length - 1",
                              aria-label="下に移動"
                            )
                              i.fas.fa-chevron-down
                    td.text-center.align-middle(
                      v-for="(collection, qIdx) in questionSlots",
                      :key="qIdx",
                      :class="{ 'question-slot-empty': !collection }"
                    )
                      template(v-if="collection")
                        | {{ getPatternChoiceForTable(pattern, collection) }}
                      template(v-else)
                        | -
                    td.text-center.align-middle
                      img.result-table-image(v-if="pattern.imageData", :src="pattern.imageData")
                      template(v-else-if="pattern.nextScenarioPopupId")
                        template(v-if="pattern.nextScenarioPopupStatus === 'deleted'")
                          .small.text-muted 接続先削除済み
                        template(v-else-if="pattern.nextScenarioPopupStatus === 'hidden'")
                          .small.text-muted 接続先非表示
                        template(v-else)
                          template(v-for="info in [getNextPopupInfo(pattern)]")
                            img.result-table-image.mb-1(v-if="info.thumbnail_url", :src="info.thumbnail_url")
                            .small.text-truncate.text-center.result-text
                              | {{ info.name || '(未設定)' }}
                      template(v-else-if="pattern.iframeEmbedUrl")
                        .small.text-muted iframe
                        .small.text-truncate.text-muted.text-center.result-text(:title="pattern.iframeEmbedUrl")
                          | {{ pattern.iframeEmbedUrl }}
                      template(v-else)
                        .text-muted.small 画像無し
                        template(v-if="pattern.noLink")
                          .small.text-muted リンクなし
                        template(v-else-if="pattern.link")
                          .small リンクあり{{ linkTabLabel(pattern) }}
                          .small.text-truncate.text-muted.text-center.result-text(:title="pattern.link")
                            | {{ pattern.link }}
                    td.text-center.align-middle
                      .scenario-actions
                        span.custom-tooltip.tooltip-top-left(:data-tooltip="editingTooltip() || 'シナリオを編集'")
                          button.btn.btn-primary.btn-sm.btn-icon(
                            @click="openPatternEditor(pattern, index)",
                            :disabled="isEditing",
                            aria-label="シナリオを編集"
                          )
                            i.fas.fa-pencil-alt
                        span.custom-tooltip.tooltip-top-left(:data-tooltip="editingTooltip() || 'シナリオを削除'")
                          button.btn.btn-danger.btn-sm.btn-icon(
                            @click="deletePattern(pattern, index)",
                            :disabled="isEditing",
                            aria-label="シナリオを削除"
                          )
                            i.fas.fa-trash-alt
          
          // 回答シナリオの設定・編集エリア
          .card.mt-4.mb-4(v-if="showPatternEditor")
            .card-header.bg-light
              h5.card-title.mb-0 回答シナリオ「{{ editingPatternLabel }}」の設定・編集
            .card-body
              .row.mb-4
                // 1列目：設問ラベル＋ボタン選択（未登録の設問枠はグレーアウト）
                .col-md-6
                  .pattern-select-column(
                    v-for="(collection, qIdx) in questionSlots",
                    :key="`edit-select-${qIdx}`",
                    :class="{ 'question-slot-empty': !collection }"
                  )
                    span.pattern-question-label 設問{{ qIdx + 1 }}
                    select.form-control.form-control-sm(
                      v-if="collection",
                      v-model="patternEditorChoices[qIdx]",
                      @change="onPatternChoiceChange(qIdx)",
                      :disabled="isPatternChoiceDisabled(qIdx)"
                    )
                      option(value="") *
                      option(
                        v-for="(choice, cIdx) in collection.choices.filter(c => !c._destroy)",
                        :key="choice.id || choice.temp_id",
                        :value="choice.id || choice.temp_id"
                      ) ボタン{{ cIdx + 1 }}
                      option(v-if="qIdx > 0", value="none") なし
                    select.form-control.form-control-sm(v-else, disabled)
                      option(value="") -

                // 2列目：計○通り（中央配置）
                .col-md-3
                  .d-flex.align-items-center.justify-content-center.h-100
                    strong 計 {{ calculatePatternEditorCoverage() }} 通り
              
              .text-muted.small.mt-2.mb-0
                | ※条件に含めない場合は「*」を選択してください。
                br
                | ※「なし」を選択すると、その設問以降はスキップされ、結果画像が表示されます。

              .mt-4
                h6 結果設定
                  help-tooltip(html-key='scenarioResultSetting')
                .form-check.mb-2
                  input#result_image_none.form-check-input(
                    type="radio",
                    v-model="patternEditorResultType",
                    :value="'image_none'"
                  )
                  label.form-check-label(for="result_image_none") 結果画像なし
                .form-check.mb-2
                  input#result_image_yes.form-check-input(
                    type="radio",
                    v-model="patternEditorResultType",
                    :value="'image_yes'"
                  )
                  label.form-check-label(for="result_image_yes") 結果画像あり
                .form-check.mb-2
                  input#next_popup.form-check-input(
                    type="radio",
                    v-model="patternEditorResultType",
                    :value="'next_popup'"
                  )
                  label.form-check-label(for="next_popup") シナリオポップアップに接続
                
                .form-check.mb-2(v-if="iframe_enabled")
                  input#result_iframe.form-check-input(
                    type="radio",
                    v-model="patternEditorResultType",
                    :value="'iframe'"
                  )
                  label.form-check-label(for="result_iframe") iframe
                
                template(v-if="patternEditorResultType === 'image_none'")
                  .mt-3(style="background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 0.25rem; padding: 12px; text-align: center;")
                    | そのままリンクに遷移します
                
                // 結果画像ありの場合
                template(v-if="patternEditorResultType === 'image_yes'")
                  .form-group.mt-3
                    input(type="file", @change="handlePatternResultImageUpload", accept=".jpg,.jpeg,.gif,.png,.webp", ref="patternImageInput")
                  .image-preview.mt-2(v-if="patternEditorImagePreview")
                    img.img-fluid(:src="patternEditorImagePreview", style="max-height: 200px;")
                
                template(v-if="patternEditorResultType === 'next_popup'")
                  .form-group.mt-3
                    .d-flex.align-items-start.justify-content-between
                      .d-flex.align-items-start(v-if="selectedPopupInfo" style="min-width: 0; overflow: hidden;")
                        .mr-3(style="width: 120px; flex-shrink: 0;")
                          img.img-fluid.rounded(
                            v-if="selectedPopupInfo.thumbnail_url"
                            :src="selectedPopupInfo.thumbnail_url"
                          )
                          .bg-secondary.d-flex.align-items-center.justify-content-center.rounded(
                            v-else
                            style="width: 120px; height: 80px; color: white; font-size: 12px;"
                          ) サムネイル
                        div(style="min-width: 0;")
                          div.text-truncate
                            strong {{ selectedPopupInfo.name }}
                          div.text-muted.small(v-if="selectedPopupInfo.questions_count !== null")
                            | 設問数：{{ selectedPopupInfo.questions_count }}　|　回答シナリオ数：{{ selectedPopupInfo.answers_count }}
                          div.text-muted.small(v-if="selectedPopupInfo.connected_from")
                            | 接続状況：{{ selectedPopupInfo.connected_from.length ? selectedPopupInfo.connected_from.join('、') : 'なし' }}
                      span(v-else) 未選択
                      .flex-shrink-0.ml-3
                        button.btn.btn-sm.btn-primary.mr-2(@click="openPopupSelectionModal") 編集
                        button.btn.btn-sm.btn-danger(@click="onPopupCleared") 解除
                    scenario-popup-selection-modal(
                      :visible="showPopupSelectionModal"
                      :options="chainedPopupOptions"
                      :current-popup-id="patternEditorNextScenarioPopupId"
                      :source-popup-id="scenario_popup_id"
                      @select="onPopupSelected"
                      @close="showPopupSelectionModal = false"
                    )
                
                template(v-if="patternEditorResultType === 'iframe' && iframe_enabled")
                  .form-group.mt-3
                    label(for="pattern_iframe_url") iframe URL
                    input#pattern_iframe_embed_url.form-control(
                      type="text",
                      v-model="patternEditorIframeEmbedUrl",
                      placeholder="https://example.com/embed"
                    )
                  .form-check.mt-2
                    input#pattern_iframe_with_parameters_flag.form-check-input(
                      type="checkbox",
                      v-model="patternEditorIframeWithParameters"
                    )
                    label.form-check-label(for="pattern_iframe_with_parameters_flag") パラメータの引継ぎをする
                template(v-if="patternEditorResultType !== 'next_popup' && patternEditorResultType !== 'iframe'")
                  // リンク設定（画像あり・なし共通）
                  ResultImageLinkSetting(
                    ref="patternLinkSetting"
                    :target_blank="patternEditorLinkSettings.targetBlank"
                    :inner_link="patternEditorLinkSettings.innerLink"
                    :no_link="patternEditorLinkSettings.noLink"
                    :link="patternEditorLinkSettings.link"
                    :with_parameters="patternEditorLinkSettings.withParameters"
                    :auto_transition="patternEditorLinkSettings.autoTransition"
                    :is_upper_master_plan="is_upper_master_plan"
                    :hide_no_link="!patternEditorHasImage"
                    :has_result_image="patternEditorHasImage"
                  )
              
              // 保存ボタン
              .mt-4.d-flex.justify-content-end
                button.btn.btn-primary.mr-2(@click="savePattern") 保存する
                button.btn.btn-secondary(@click="closePatternEditor") キャンセル
          
          // デフォルト結果画像の登録セクション
          .card.mt-4.mb-4
            .card-header.bg-light
              .d-flex.justify-content-between.align-items-center
                h5.card-title.mb-0
                  | デフォルトの結果
                  help-tooltip(html-key='scenarioDefaultResult')
                span.custom-tooltip.tooltip-top-left(v-if="!showDefaultPatternForm && defaultPatternObject", :data-tooltip="getDefaultEditTooltip()")
                  button.btn.btn-primary.btn-sm(
                    @click="editDefaultPattern",
                    :disabled="isEditing || !hasImagesWithChoices"
                  ) 編集
            .card-body
              p.text-muted.small.mb-3 上記のシナリオに一致しない場合に表示されるコンテンツです。

              // ガイダンスメッセージ（ボタンがない場合）
              .alert.alert-warning.mb-3(v-if="!hasImagesWithChoices && !defaultPatternObject")
                i.fas.fa-info-circle.mr-2
                | 設問画像とボタンを追加すると、デフォルト結果画像を登録できます

              // 保存済みプレビュー表示
              template(v-if="!showDefaultPatternForm && defaultPatternObject")
                .default-pattern-preview
                  .default-pattern-preview-row
                    .default-pattern-preview-media
                      template(v-if="defaultHasImage && defaultPatternImage")
                        img(:src="defaultPatternImage", alt="デフォルト結果画像")
                      template(v-else-if="defaultPatternObject.iframeEmbedUrl")
                        .text-muted.p-3.bg-light.rounded.text-center iframe
                      template(v-else)
                        .text-muted.p-3.bg-light.rounded.text-center 結果画像なし
                    .default-pattern-preview-meta
                      .mb-2
                        strong リンク設定
                      ul.list-unstyled.small.text-muted.mb-0
                        template(v-if="defaultPatternObject.iframeEmbedUrl")
                          li
                            | リンク先：{{ defaultPatternObject.iframeEmbedUrl }}
                        template(v-else-if="defaultLinkSettings.noLink")
                          li リンクなし
                        template(v-else-if="defaultLinkSettings.link")
                          li
                            | リンク先: {{ defaultLinkSettings.link }}
                            span.ml-2(v-if="defaultLinkSettings.targetBlank") （別タブ）
                            span.ml-2(v-if="defaultLinkSettings.innerLink") （同じタブ）
                        template(v-else)
                          li リンク未設定

              // 編集フォーム
              template(v-else-if="hasImagesWithChoices")
                .mt-4
                  h6 結果設定
                  .form-check.mb-2
                    input#default_result_image_none.form-check-input(
                      type="radio",
                      v-model="defaultResultType",
                      :value="'image_none'"
                    )
                    label.form-check-label(for="default_result_image_none") 結果画像なし
                  .form-check.mb-2
                    input#default_result_image_yes.form-check-input(
                      type="radio",
                      v-model="defaultResultType",
                      :value="'image_yes'"
                    )
                    label.form-check-label(for="default_result_image_yes") 結果画像あり
                  .form-check.mb-2(v-if="iframe_enabled")
                    input#default_result_iframe.form-check-input(
                      type="radio",
                      v-model="defaultResultType",
                      :value="'iframe'"
                    )
                    label.form-check-label(for="default_result_iframe") iframe

                  // 結果画像なしの場合のメッセージ
                  template(v-if="defaultResultType === 'image_none'")
                    .mt-3(style="background-color: #f8f9fa; border: 1px solid #dee2e6; border-radius: 0.25rem; padding: 12px; text-align: center;")
                      | そのままリンクに遷移します

                  // 結果画像ありの場合
                  template(v-if="defaultHasImage")
                    .form-group.mt-3
                      input(type="file", @change="handleDefaultPatternUpload", accept=".jpg,.jpeg,.gif,.png,.webp", ref="defaultPatternImageInput")
                    .image-preview.mt-2(v-if="defaultPatternImage || tempImages.defaultPatternPreview")
                      img.img-fluid(:src="tempImages.defaultPatternPreview || defaultPatternImage", style="max-height: 200px;")

                  // iframe URLの入力欄
                  template(v-if="defaultResultType === 'iframe' && iframe_enabled")
                    .form-group.mt-3
                      label(for="default_iframe_embed_url") iframe URL
                      input#default_iframe_embed_url.form-control(
                        type="text",
                        v-model="defaultIframeEmbedUrl",
                        placeholder="https://example.com/embed"
                      )
                    .form-check.mt-2
                      input#default_iframe_with_parameters_flag.form-check-input(
                        type="checkbox",
                        v-model="defaultIframeWithParameters"
                      )
                      label.form-check-label(for="default_iframe_with_parameters_flag") パラメータの引継ぎをする

                // リンク設定（iframeの場合は非表示）
                template(v-if="defaultResultType !== 'iframe'")
                  ResultImageLinkSetting(
                    ref="defaultLinkSetting"
                    :target_blank="defaultLinkSettings.targetBlank"
                    :inner_link="defaultLinkSettings.innerLink"
                    :no_link="defaultLinkSettings.noLink"
                    :link="defaultLinkSettings.link"
                    :with_parameters="defaultLinkSettings.withParameters"
                    :auto_transition="defaultLinkSettings.autoTransition"
                    :is_upper_master_plan="is_upper_master_plan"
                    :hide_no_link="!defaultHasImage"
                    :has_result_image="defaultHasImage"
                  )
                .mt-3.d-flex.justify-content-end
                  button.btn.btn-primary(@click="saveDefaultPattern") 保存する
                  button.btn.btn-secondary.ml-2(@click="cancelDefaultPattern", v-if="defaultPatternObject") キャンセル
              .text-danger.mt-2(v-if="defaultPatternError") デフォルトの結果画像を登録してください
    
    .scenario-editor-footer-spacer
    .scenario-editor-footer
      .alert.alert-warning.py-2.px-3.mb-0.scenario-editor-footer-message(v-if="isEditing")
        | 回答の結果を「保存」または「キャンセル」してください
      button.btn.btn-default.px-4(
        type="button",
        :disabled="isEditing",
        @click="leaveWithoutSaving"
      ) 保存せず戻る
      span.custom-tooltip.tooltip-left(:data-tooltip="getUpdateTooltip()")
        button.btn.btn-success.px-5(
          :disabled="saving || isEditing || !hasImagesWithChoices || !defaultPatternObject",
          @click="validateAndSave"
        )
          | {{ saving ? '更新中です...' : '変更を保存' }}
</template>

<script>
import { creativeFormMixin } from '../mixins/creative_form';
import UpgradeUpperMaster from "./modals/upgrade_upper_master.vue";
import ResultImageLinkSetting from "./result_image_link_setting.vue";
import LoadingOverlay from "./parts/loading_overlay.vue";
import CreativeApi from '../apis/creative'
import ScenarioPopupSelectionModal from "./modals/scenario_popup_selection_modal.vue";
import ScenarioPopupSimulator from "./scenario_popup_simulator.vue";
import { linkTabLabel } from '../utils/linkTabLabel';

// 定数定義
const MAX_SCENARIO_IMAGES = 5;
const MAX_CHOICES_PER_IMAGE = 5;
const MAX_PATTERNS = 10;

export default {
  mixins: [creativeFormMixin],
  props: {
    pop_up_animations: {
      type: Object,
      default: null
    },
    is_upper_master_plan: {
      type: Boolean,
      default: false
    },
    scenario_popup_id: {
      type: String,
      default: null
    },
    hide_pr: { type: Boolean, default: false },
    edit_data: {
      type: Object,
      default: null
    },
    scenario_popup_url_token: {
      type: String,
      default: null
    },
    is_copy: {
      type: Boolean,
      default: false
    },
    is_chained: {
      type: Boolean,
      default: false
    },
    iframe_enabled: {
      type: Boolean,
      default: !!window.kengaIframeEnabled
    },
  },
  data() {
    return {
      pcMaxWidth: '500',
      animation: '',
      collections: [],
      collectionErrors: [],
      currentCollection: null,
      originalCollection: null,
      originalCollectionIndex: -1,
      pendingDeleteCollectionId: null,
      pendingReplaceCollectionId: null,
      replaceImageFile: null,
      replaceImagePreview: null,
      isDrawing: false,
      startPos: { x: 0, y: 0 },
      currentArea: { x: 0, y: 0, width: 0, height: 0 },
      showChoiceSettings: true,
      showEditor: true,
      coverImage: null,
      loadingImage: null,
      coverImageError: false,
      hasError: false,
      apiErrorMessages: [],
      // defaultシナリオ用
      defaultPatternImage: null,
      defaultPatternError: false,
      defaultResultType: 'image_yes',
      defaultIframeEmbedUrl: '',
      // iframe用パラメータ引継ぎ（リンク設定のwithParametersとは別管理。結果種別の切替時に値が混ざらないようにするため）
      defaultIframeWithParameters: false,
      // 一時保存用変数（各エディターで選択中の画像ファイル）
      tempImages: {
        defaultPattern: null,
        defaultPatternPreview: '',
        animation: null,
        patternEditor: null
      },
      defaultPatternObject: null,
      showDefaultPatternForm: false,
      showDevice: 'both',
      showLoadingImage: false,
      choiceAction: 'add_scenario',
      linkError: false,
      linkType: 'target_blank',
      imageMaxWidth: '',
      // デフォルトシナリオのリンク設定
      defaultLinkSettings: {
        targetBlank: false,
        innerLink: false,
        noLink: false,
        link: '',
        withParameters: false,
        autoTransition: false
      },
      // 初期状態保存用（キャンセル時の復元用）
      originalDefaultPatternImage: null,
      originalDefaultPatternObject: null,
      originalDefaultLinkSettings: null,
      originalDefaultIframeEmbedUrl: '',
      saving: false,
      // アニメーション画像設定用
      showAnimationModal: false,
      currentChoiceId: null,
      currentCollectionId: null,
      animationImagePreview: null,
      // 分岐回答用
      showPatternEditor: false,
      editingPattern: null,
      editingPatternIndex: -1,
      patternEditorChoices: {},
      patternEditorResultType: 'image_yes',
      patternEditorNextScenarioPopupId: null,
      patternEditorImagePreview: null,
      // シナリオエディターのリンク設定
      patternEditorLinkSettings: {
        targetBlank: false,
        innerLink: false,
        noLink: false,
        link: '',
        withParameters: false,
        autoTransition: false
      },
      // シナリオポップアップパターン用
      scenarioPopupPatterns: [],
      showPopupSelectionModal: false,
      chainedPopupOptions: [],
      popupOptionsLoading: false,
      patternEditorIframeEmbedUrl: '',
      // iframe用パラメータ引継ぎ（patternEditorLinkSettings.withParametersとは別管理。結果種別の切替時に値が混ざらないようにするため）
      patternEditorIframeWithParameters: false,
    };
  },
  computed: {
    isChainedPopup() {
      return this.is_chained || (this.edit_data && this.edit_data.isChained) || false;
    },
    getButtonName() {
      return (image) => {
        const collection = this.collections.find(c => c.id === image.scenarioImageId || c.temp_id === image.scenarioImageId);
        if (collection && collection.choices) {
          const choice = collection.choices.find(choice => choice.id === image.choiceId || choice.temp_id === image.choiceId);
          return choice ? choice.buttonName : 'それ以外の場合';
        } else {
          return 'それ以外の場合';
        }
      };
    },
    canAddMoreImages() {
      return this.collections.filter(c => !c._destroy).length < MAX_SCENARIO_IMAGES;
    },
    canAddMoreChoices() {
      return this.currentCollection ? this.currentCollection.choices.filter(choice => !choice._destroy).length < MAX_CHOICES_PER_IMAGE : false;
    },
    maxScenarioImages() {
      return MAX_SCENARIO_IMAGES;
    },
    maxChoicesPerImage() {
      return MAX_CHOICES_PER_IMAGE;
    },
    maxPatterns() {
      return MAX_PATTERNS;
    },
    selectedCollection() {
      return this.collections.find(c =>
        c.id === this.selectedScenarioImageId || c.temp_id === this.selectedScenarioImageId
      ) || { choices: [] };
    },
    hasImagesWithChoices() {
      return this.activeCollections.some(collection =>
        collection.choices && collection.choices.some(choice => !choice._destroy)
      );
    },
    // 編集状態の一元管理
    isEditingCollection() {
      return this.showEditor && this.currentCollection;
    },
    isEditingPattern() {
      return this.showPatternEditor;
    },
    isEditingDefault() {
      return this.showDefaultPatternForm && this.defaultPatternObject;
    },
    isEditing() {
      return this.isEditingCollection || this.isEditingPattern || this.isEditingDefault;
    },
    hasActivePatterns() {
      // デフォルト以外のアクティブなパターンが存在するかチェック
      return this.scenarioPopupPatterns.some(p => !p._destroy && !p.isDefault);
    },
    isLastCollection() {
      // 削除しようとしている設問が最後の設問かどうか
      if (!this.pendingDeleteCollectionId) return false;
      const lastCollection = this.activeCollections[this.activeCollections.length - 1];
      if (!lastCollection) return false;
      return lastCollection.id === this.pendingDeleteCollectionId ||
             lastCollection.temp_id === this.pendingDeleteCollectionId;
    },
    popUpAnimation() {
      return this.$store.state.creative.preview_list.pop_up_animation;
    },
    editingPatternLabel() {
      if (this.editingPatternIndex === -1) {
        // 新規追加の場合、次のラベルを返す
        const currentCount = this.scenarioPopupPatterns.filter(p => !p._destroy && !p.isDefault).length;
        return this.getPatternLabel(currentCount);
      }
      return this.getPatternLabel(this.editingPatternIndex);
    },
    activeCollections() {
      return this.collections.filter(collection => !collection._destroy);
    },
    // 設問枠は常に最大数分確保し、未登録分は null（グレーアウト表示用）
    questionSlots() {
      const slots = [];
      for (let i = 0; i < MAX_SCENARIO_IMAGES; i++) {
        slots.push(this.activeCollections[i] || null);
      }
      return slots;
    },
    // 保存済みコレクション（新規作成中のコレクションのみ除外、既存の編集中は含める）
    savedCollections() {
      if (!this.showEditor || !this.currentCollection) {
        return this.activeCollections;
      }
      // 既存コレクションの編集中はそのまま表示
      if (this.originalCollection) {
        return this.activeCollections;
      }
      // 新規コレクション作成中のみ除外
      const currentId = this.currentCollection.id || this.currentCollection.temp_id;
      return this.activeCollections.filter(c => {
        const cId = c.id || c.temp_id;
        return cId !== currentId;
      });
    },
    hasSavedImagesWithChoices() {
      return this.savedCollections.some(c => this.activeChoices(c).length > 0);
    },
    activePatterns() {
      return this.scenarioPopupPatterns.filter(p => !p._destroy && !p.isDefault);
    },
    patternEditorHasImage() {
      return this.patternEditorResultType === 'image_yes';
    },
    selectedPopupInfo() {
      if (!this.patternEditorNextScenarioPopupId) return null
      const found = this.chainedPopupOptions.find(p => p.id === this.patternEditorNextScenarioPopupId)
      if (found) return found
      if (!this.editingPattern) return null
      return {
        name: this.editingPattern.nextScenarioPopupName,
        thumbnail_url: this.editingPattern.nextScenarioPopupThumbnailUrl,
        questions_count: null,
        answers_count: null
      }
    },
    selectedPopupName() {
      return this.selectedPopupInfo ? this.selectedPopupInfo.name : null
    },
    defaultHasImage() {
      return this.defaultResultType === 'image_yes'
    }
  },
  watch: {
    hasSavedImagesWithChoices(newVal) {
      if (!newVal) {
        this.resetPatternsAndDefault();
      }
    },
    showLoadingImage(newVal) {
      // チェックボックスの状態をストアに保存
      this.updateScenarioField("show_loading_image", newVal);

      // チェックが外されて、既存のローディング画像がある場合は削除フラグを立てる
      if (!newVal && this.loadingImage) {
        this.$store.commit("creative/setDeleteLoadingImage", true);
      } else {
        this.$store.commit("creative/setDeleteLoadingImage", false);
      }
    }
  },
  components: { UpgradeUpperMaster, ResultImageLinkSetting, LoadingOverlay, ScenarioPopupSelectionModal, ScenarioPopupSimulator },
  mounted() {
    const showPc = this.showDevice === 'both' || this.showDevice === 'pc';
    const showSp = this.showDevice === 'both' || this.showDevice === 'sp';
    this.$store.commit("creative/setPreviewListShowPc", showPc);
    this.$store.commit("creative/setPreviewListShowSp", showSp);
    this.$el.addEventListener('mouseover', this.pinFloatingTooltip);
  },
  beforeDestroy() {
    this.$el.removeEventListener('mouseover', this.pinFloatingTooltip);
  },
  created() {
    console.log("this.edit_data", this.edit_data);
    this.$store.commit("creative/updateScenarioField", {
      field: "scenario_popup_id",
      value: this.scenario_popup_id
    });

    // 編集用データがある場合、初期値に上書きする
    if (this.edit_data) {
      this.$store.commit("creative/updateScenarioField", {
        field: "id",
        value: this.edit_data.id
      });
      // PC画像サイズの初期値
      this.pcMaxWidth = this.edit_data.pcMaxWidth || this.pcMaxWidth;
      this.$store.commit("creative/setPreviewListPopUpMaxWidth", this.pcMaxWidth);
      // 表示デバイス（"both", "pc", "sp" のいずれか）
      this.showDevice = this.edit_data.showDevice || this.showDevice;

      // コレクション（シナリオ画像とそのボタン）
      if (this.edit_data.collections) {
        try {
          this.collections = this.parseAndClone(this.edit_data.collections) || [];

          // アニメーション画像データの初期設定
          this.collections.forEach(collection => {
            if (collection.choices && Array.isArray(collection.choices)) {
              collection.choices.forEach(choice => {
                if (choice.animationImageData) {
                  choice.animationImagePreview = choice.animationImageData;
                }
              });
            }
          });

          this.updateScenarioField("collections", this.parseAndClone(this.collections));
        } catch (error) {
          console.error('コレクションのパースエラー', error);
        }
      }

      // シナリオポップアップパターン（新横持ちテーブル）
      if (this.edit_data.scenarioPopupPatterns) {
        try {
          this.scenarioPopupPatterns = this.parseAndClone(this.edit_data.scenarioPopupPatterns) || [];
          this.updateScenarioField("scenario_popup_patterns", this.parseAndClone(this.scenarioPopupPatterns));
        } catch (error) {
          console.error('シナリオポップアップパターンのパースエラー', error);
        }
      }

      // プレビュー画像を設定（設問1の画像）
      if (this.collections.length > 0 && this.collections[0].imageData) {
        this.$store.commit("creative/setPreviewListImage", this.collections[0].imageData);
        this.$store.commit("creative/setPreviewListPcImage", this.collections[0].imageData);
      }

      // デフォルト結果画像（is_default: true のパターン）
      const defaultPattern = this.scenarioPopupPatterns.find(p => p.isDefault);
      if (defaultPattern) {
        this.defaultPatternImage = defaultPattern.imageData || this.defaultPatternImage;
        this.defaultPatternObject = defaultPattern;
        if (defaultPattern.iframeEmbedUrl) {
          this.defaultResultType = 'iframe';
          this.defaultIframeEmbedUrl = defaultPattern.iframeEmbedUrl;
          this.defaultIframeWithParameters = defaultPattern.withParameters || false;
        } else {
          this.defaultResultType = defaultPattern.imageData ? 'image_yes' : 'image_none';
          this.defaultIframeWithParameters = false;
        }
        this.showDefaultPatternForm = false;
        this.$store.commit("creative/updateScenarioField", {
          field: "default_pattern_image",
          value: this.defaultPatternImage
        });
        // デフォルト結果画像のリンク設定
        this.defaultLinkSettings = {
          targetBlank: defaultPattern.targetBlank ?? this.defaultLinkSettings.targetBlank,
          innerLink: defaultPattern.innerLink ?? this.defaultLinkSettings.innerLink,
          noLink: defaultPattern.noLink ?? this.defaultLinkSettings.noLink,
          link: defaultPattern.link || this.defaultLinkSettings.link,
          withParameters: defaultPattern.withParameters ?? this.defaultLinkSettings.withParameters,
          autoTransition: defaultPattern.autoTransition ?? this.defaultLinkSettings.autoTransition
        };

        // 初期状態を保存（キャンセル時の復元用）
        this.originalDefaultPatternImage = this.defaultPatternImage;
        this.originalDefaultPatternImageObject = this.parseAndClone(defaultPattern);
        this.originalDefaultLinkSettings = { ...this.defaultLinkSettings };
        this.originalDefaultIframeEmbedUrl = this.defaultIframeEmbedUrl;
      }

      // ローディング画像
      this.loadingImage = this.edit_data.loadingImage || this.loadingImage;
      this.$store.commit("creative/updateScenarioField", {
        field: "loading_image",
        value: this.loadingImage
      });
      // ローディング画像がある場合、showLoadingImageをtrueに設定
      if (this.loadingImage) {
        this.showLoadingImage = true;
        this.$store.commit("creative/updateScenarioField", {
          field: "show_loading_image",
          value: true
        });
      }

      this.$store.commit("creative/updateScenarioField", {
        field: "default_target_blank",
        value: this.defaultLinkSettings.targetBlank
      });
      this.$store.commit("creative/updateScenarioField", {
        field: "default_inner_link",
        value: this.defaultLinkSettings.innerLink
      });
      this.$store.commit("creative/updateScenarioField", {
        field: "default_no_link",
        value: this.defaultLinkSettings.noLink
      });
      this.$store.commit("creative/updateScenarioField", {
        field: "default_link",
        value: this.defaultLinkSettings.link
      });
      this.$store.commit("creative/updateScenarioField", {
        field: "default_with_parameters",
        value: this.defaultLinkSettings.withParameters
      });
      this.$store.commit("creative/updateScenarioField", {
        field: "default_auto_transition",
        value: this.defaultLinkSettings.autoTransition
      });
    }

    this.preloadChainedPopupOptions()
  },
  methods: {
    // ===== ツールチップメソッド =====
    pinFloatingTooltip(event) {
      const el = event.target && event.target.closest
        ? event.target.closest('.custom-tooltip[data-tooltip]')
        : null;
      if (!el || !this.$el.contains(el) || el.getAttribute('data-tooltip') === '') return;
      const rect = el.getBoundingClientRect();
      el.style.setProperty('--tt-left', `${Math.round(rect.left)}px`);
      el.style.setProperty('--tt-top', `${Math.round(rect.top)}px`);
      el.style.setProperty('--tt-width', `${Math.round(rect.width)}px`);
      el.style.setProperty('--tt-height', `${Math.round(rect.height)}px`);
    },
    leaveWithoutSaving() {
      if (this.isEditing) return;
      window.location.href = '/users/popups';
    },
    // 編集中の場合に共通で返すツールチップ（追加条件がある場合は ...extraMessages に渡す）
    editingTooltip(...extraMessages) {
      if (this.isEditing) return '回答の結果を「保存」または「キャンセル」してください';
      for (const msg of extraMessages) {
        if (msg) return msg;
      }
      return '';
    },
    getImageInputTooltip() {
      return this.editingTooltip(
        !this.canAddMoreImages && '設問画像は最大5枚までです'
      );
    },
    getPatternPriorityUpTooltip(index) {
      return this.editingTooltip(index === 0 && '既に一番上です');
    },
    getPatternPriorityDownTooltip(index) {
      return this.editingTooltip(
        index === this.activePatterns.length - 1 && '既に一番下です'
      );
    },
    getPatternAddTooltip() {
      return this.editingTooltip(
        !this.hasImagesWithChoices && '設問画像とボタンを追加してください',
        this.activePatterns.length >= MAX_PATTERNS && `シナリオは最大${MAX_PATTERNS}個までです`
      );
    },
    getDefaultEditTooltip() {
      return this.editingTooltip(
        !this.hasImagesWithChoices && '設問画像とボタンを追加してください'
      );
    },
    getUpdateTooltip() {
      if (this.saving) return '';
      return this.editingTooltip(
        !this.hasImagesWithChoices && '設問画像とボタンを追加してください',
        !this.defaultPatternObject && 'デフォルトの結果を登録してください'
      );
    },

    // mixin の setShowDevice は画像ストアをクリアするが、シナリオポップアップでは不要なためオーバーライド
    setShowDevice(device) {
      this.showDevice = device;
      this.$store.commit("creative/setPreviewListShowPc", device === 'both' || device === 'pc');
      this.$store.commit("creative/setPreviewListShowSp", device === 'both' || device === 'sp');
    },

    // ===== ヘルパーメソッド =====
    parseAndClone(data) {
      if (!data) return null;
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return JSON.parse(JSON.stringify(parsed));
    },
    updateScenarioField(field, value) {
      this.$store.commit("creative/updateScenarioField", { field, value });
    },
    // ローカルの collections から choice を検索
    findLocalChoice(collectionId, choiceId) {
      const collection = this.findCollectionById(collectionId);
      if (!collection || !collection.choices) return null;
      return collection.choices.find(ch => ch.id === choiceId || ch.temp_id === choiceId);
    },
    // コレクションをIDで検索
    findCollectionById(id) {
      return this.collections.find(c => c.id === id || c.temp_id === id);
    },
    // コレクションのインデックスをIDで検索
    findCollectionIndexById(id) {
      return this.collections.findIndex(c => c.id === id || c.temp_id === id);
    },
    // activeCollections内でのインデックスを取得
    findActiveCollectionIndex(id) {
      return this.activeCollections.findIndex(
        c => (c.id && c.id === id) || (c.temp_id && c.temp_id === id)
      );
    },
    // パターンの該当設問のchoice_idをリセット
    resetPatternChoiceIds(collectionIndex, value) {
      if (collectionIndex === -1) return;
      const qKey = `q${collectionIndex + 1}ChoiceId`;
      this.scenarioPopupPatterns.forEach(pattern => {
        if (!pattern._destroy) {
          this.$set(pattern, qKey, value);
        }
      });
      this.updateScenarioField("scenario_popup_patterns", [...this.scenarioPopupPatterns]);
    },
    updatePcMaxWidth(e) {
      const value = (e && e.target && e.target.value != null)
        ? e.target.value
        : this.pcMaxWidth;
      this.pcMaxWidth = String(value);
      this.$store.commit('creative/setPreviewListPopUpMaxWidth', this.pcMaxWidth);
    },
    onStartDrawing(event) {
      if (event.button !== 0 || !this.canAddMoreChoices) return;
      const rect = event.currentTarget.getBoundingClientRect();
      this.startPos = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
      this.isDrawing = true;
      this.updateScenarioField("start_pos", this.startPos);
      this.updateScenarioField("is_drawing", true);
    },
    onUpdateDrawing(event) {
      if (!this.isDrawing || !this.canAddMoreChoices) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const currentX = event.clientX - rect.left;
      const currentY = event.clientY - rect.top;
      this.currentArea = {
        x: (Math.min(this.startPos.x, currentX) / rect.width) * 100,
        y: (Math.min(this.startPos.y, currentY) / rect.height) * 100,
        width: (Math.abs(currentX - this.startPos.x) / rect.width) * 100,
        height: (Math.abs(currentY - this.startPos.y) / rect.height) * 100
      };
      this.updateScenarioField("current_area", this.currentArea);
    },
    onStopDrawing() {
      if (this.isDrawing && this.currentArea.width > 5 && this.currentArea.height > 5 && this.currentCollection && this.canAddMoreChoices) {
        const newChoice = {
          temp_id: this.currentCollection.choices.length + 1,
          ...this.currentArea,
          buttonName: `ボタン${this.currentCollection.choices.filter(choice => !choice._destroy).length + 1}`,
        };
        this.currentCollection.choices.push(newChoice);
        this.clearErrorsForCollection(this.currentCollection.id);
      }
      this.isDrawing = false;
      this.currentArea = { x: 0, y: 0, width: 0, height: 0 };
      this.updateScenarioField("is_drawing", false);
      this.updateScenarioField("current_area", this.currentArea);
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const previewUrl = URL.createObjectURL(file);
      const newCollection = {
        temp_id: this.collections.length + 1,
        name: file.name,
        imageData: previewUrl,
        image: file,
        choices: []
      };
      this.collections.push(newCollection);
      this.currentCollection = newCollection;
      this.showEditor = true;
      this.updateScenarioField("collections", this.parseAndClone(this.collections));
      if(this.collections.length  == 1){
        this.$store.commit("creative/setPreviewListImage", previewUrl);
        this.$store.commit("creative/setPreviewListPcImage", previewUrl);
      }
    },
    handleLoadingImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const previewUrl = URL.createObjectURL(file);
      this.loadingImage = previewUrl;
      event.target.value = '';
      this.updateScenarioField("loading_image", file);
      // 新しい画像がアップロードされたので削除フラグをリセット
      this.$store.commit("creative/setDeleteLoadingImage", false);
    },
    handleDefaultPatternUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const previewUrl = URL.createObjectURL(file);
      // 一時保存用に設定
      this.tempImages.defaultPattern = file;
      this.tempImages.defaultPatternPreview = previewUrl;
      // プレビュー表示用
      this.defaultPatternImage = previewUrl;
      this.defaultPatternError = false;
    },
    deleteAllChoices(collectionId) {
      // 削除対象のコレクションIDを一時保存してモーダルを表示
      this.pendingDeleteCollectionId = collectionId;
      this.$bvModal.show('delete-all-choices-modal');
    },
    confirmDeleteAllChoices() {
      const collectionId = this.pendingDeleteCollectionId;
      const collection = this.findCollectionById(collectionId);
      if (collection) {
        // 既存ボタンは_destroyフラグ、新規ボタンは配列から削除
        collection.choices.forEach(choice => {
          if (choice.id) {
            this.$set(choice, "_destroy", true);
          }
        });
        collection.choices = collection.choices.filter(choice => choice.id);

        // パターンの選択をワイルドカード(0)にリセット
        const collectionIndex = this.findActiveCollectionIndex(collectionId);
        this.resetPatternChoiceIds(collectionIndex, 0);

        this.updateScenarioField("collections", this.collections);
      }
      this.pendingDeleteCollectionId = null;
      this.$bvModal.hide('delete-all-choices-modal');
    },
    activeChoices(collection) {
      if (!collection || !collection.choices) return [];
      return collection.choices.filter(choice => !choice._destroy);
    },
    selectCollection(collection) {
      const collectionId = collection.id || collection.temp_id;
      this.originalCollectionIndex = this.findCollectionIndexById(collectionId);
      this.originalCollection = this.parseAndClone(collection);
      this.currentCollection = collection;
      this.showChoiceSettings = true;
      this.showEditor = true;
      this.$nextTick(() => {
        const el = this.$el.querySelector('.drag-drop-instruction');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      });
    },
    cancelCollectionEdit() {
      if (this.originalCollection && this.originalCollectionIndex !== -1) {
        // 既存の設問を編集中の場合は編集前の状態に復元
        this.$set(this.collections, this.originalCollectionIndex, this.parseAndClone(this.originalCollection));
      } else if (this.currentCollection) {
        // 新規追加の場合は画像ごと削除（キャンセル＝なかったことにする）
        const index = this.collections.findIndex(c =>
          (this.currentCollection.id && c.id === this.currentCollection.id) ||
          (this.currentCollection.temp_id && c.temp_id === this.currentCollection.temp_id)
        );
        if (index !== -1) {
          this.collections.splice(index, 1);
        }
      }
      this.showEditor = false;
      this.currentCollection = null;
      this.originalCollection = null;
      this.originalCollectionIndex = -1;
      if (this.$refs.questionImageInput) this.$refs.questionImageInput.value = '';
      // collections復元後の状態でプレビュー画像を同期（差し替えモーダル確定後にキャンセルした場合の巻き戻し）
      const firstCollection = this.activeCollections[0];
      if (firstCollection && firstCollection.imageData) {
        this.$store.commit("creative/setPreviewListImage", firstCollection.imageData);
        this.$store.commit("creative/setPreviewListPcImage", firstCollection.imageData);
      } else {
        this.$store.commit("creative/setPreviewListImage", "");
        this.$store.commit("creative/setPreviewListPcImage", "");
      }
    },
    showDeleteCollectionModal(collectionId) {
      // 削除対象のコレクションIDを一時保存してモーダルを表示
      this.pendingDeleteCollectionId = collectionId;
      this.$bvModal.show('delete-collection-modal');
    },
    confirmDeleteCollection() {
      const collectionId = this.pendingDeleteCollectionId;

      // 最後の設問を削除する場合、パターンをnull（なし）にリセット
      if (this.isLastCollection && this.hasActivePatterns) {
        const collectionIndex = this.findActiveCollectionIndex(collectionId);
        this.resetPatternChoiceIds(collectionIndex, null);
      }

      this.deleteCollection(collectionId);
      this.pendingDeleteCollectionId = null;
      this.$bvModal.hide('delete-collection-modal');
    },
    deleteCollection(collectionId) {
      const index = this.findCollectionIndexById(collectionId);
      if (index !== -1) {
        const collection = this.collections[index];
        if (collection.id) {
          this.$set(collection, "_destroy", true);
        } else {
          this.collections.splice(index, 1);
        }
        // 現在表示中のcollectionが削除された場合の再設定
        if (this.currentCollection && (this.currentCollection.id === collectionId || this.currentCollection.temp_id === collectionId)) {
          this.currentCollection = this.activeCollections[0] || null;
        }
        this.updateScenarioField("collections", this.collections);
        const firstCollection = this.activeCollections[0];
        if (firstCollection && firstCollection.imageData) {
          this.$store.commit("creative/setPreviewListImage", firstCollection.imageData);
          this.$store.commit("creative/setPreviewListPcImage", firstCollection.imageData);
        } else {
          this.$store.commit("creative/setPreviewListImage", "");
          this.$store.commit("creative/setPreviewListPcImage", "");
        }
      }
    },
    showReplaceImageModal(collectionId) {
      this.pendingReplaceCollectionId = collectionId;
      this.$bvModal.show('replace-image-modal');
    },
    handleReplaceImageSelect(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.replaceImageFile = file;
      this.replaceImagePreview = URL.createObjectURL(file);
    },
    confirmReplaceImage() {
      const collection = this.findCollectionById(this.pendingReplaceCollectionId);
      if (collection && this.replaceImageFile) {
        collection.image = this.replaceImageFile;
        collection.imageData = this.replaceImagePreview;
        this.updateScenarioField("collections", this.collections);
        if (this.collections[0] === collection) {
          this.$store.commit("creative/setPreviewListImage", this.replaceImagePreview);
          this.$store.commit("creative/setPreviewListPcImage", this.replaceImagePreview);
        }
      }
      this.resetReplaceImageModal();
      this.$bvModal.hide('replace-image-modal');
    },
    cancelReplaceImage() {
      this.replaceImageFile = null;
      this.replaceImagePreview = null;
    },
    resetReplaceImageModal() {
      this.pendingReplaceCollectionId = null;
      this.replaceImageFile = null;
      this.replaceImagePreview = null;
      if (this.$refs.replaceImageInput) {
        this.$refs.replaceImageInput.value = '';
      }
    },
    async validateAndSave() {
      this.saving = true;
      this.hasError = false;
      this.collectionErrors = {};
      this.savedCollections.forEach((collection, index) => {
        if (this.activeChoices(collection).length === 0) {
          this.$set(this.collectionErrors, index, 'ボタンは少なくとも1つ登録してください');
          this.hasError = true;
        }
      });
      if (this.defaultHasImage && !this.defaultPatternImage) {
        this.defaultPatternError = true;
        this.hasError = true;
      }
      if (this.hasError) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.saving = false;
        return false;
      }
      // 保存前にcollectionsをVuexストアに同期（Fileオブジェクトを保持したまま）
      this.$store.commit("creative/updateScenarioField", {
        field: "collections",
        value: this.collections
      });
      try {
        this.apiErrorMessages = [];
        if(this.edit_data && !this.is_copy){
          // 編集処理として扱う
          await this.$store.dispatch("creative/edit_scenario");
          location.href = `/users/popups/${this.scenario_popup_url_token}/creatives/redirect_after_update`;
        } else {
          await this.$store.dispatch("creative/create_scenario");
          location.href = `/users/popups/${this.scenario_popup_url_token}/creatives/redirect_after_create`;
        }
      } catch (error) {
        const errors = error?.response?.data?.errors;
        this.apiErrorMessages = Array.isArray(errors) && errors.length > 0
          ? errors
          : ['保存中にエラーが発生しました。'];
        this.saving = false;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    deletePattern(pattern, _index) {
      if (pattern.id) {
        // 既存のパターン（DBに保存済み）は_destroyフラグを立てる
        this.$set(pattern, "_destroy", true);
      } else {
        // 新規パターン（未保存）は配列から削除
        const actualIndex = this.scenarioPopupPatterns.findIndex(p => p === pattern);
        if (actualIndex !== -1) {
          this.scenarioPopupPatterns.splice(actualIndex, 1);
        }
      }
      this.updatePatternOrders();
      this.updateScenarioField("scenario_popup_patterns", [...this.scenarioPopupPatterns]);
    },
    movePatternUp(index) {
      const activePatterns = this.activePatterns;
      if (index <= 0) return;

      // activePatterns内での対象パターンを取得
      const currentPattern = activePatterns[index];
      const abovePattern = activePatterns[index - 1];

      // patternOrderを入れ替え
      const tempOrder = currentPattern.patternOrder;
      currentPattern.patternOrder = abovePattern.patternOrder;
      abovePattern.patternOrder = tempOrder;

      // 配列を並び替え
      this.sortPatternsByOrder();
      this.updateScenarioField("scenario_popup_patterns", [...this.scenarioPopupPatterns]);
    },
    movePatternDown(index) {
      const activePatterns = this.activePatterns;
      if (index >= activePatterns.length - 1) return;

      // activePatterns内での対象パターンを取得
      const currentPattern = activePatterns[index];
      const belowPattern = activePatterns[index + 1];

      // patternOrderを入れ替え
      const tempOrder = currentPattern.patternOrder;
      currentPattern.patternOrder = belowPattern.patternOrder;
      belowPattern.patternOrder = tempOrder;

      // 配列を並び替え
      this.sortPatternsByOrder();
      this.updateScenarioField("scenario_popup_patterns", [...this.scenarioPopupPatterns]);
    },
    sortPatternsByOrder() {
      // patternOrderで昇順ソート（isDefaultはそのまま末尾に）
      this.scenarioPopupPatterns.sort((a, b) => {
        // isDefaultは常に最後
        if (a.isDefault && !b.isDefault) return 1;
        if (!a.isDefault && b.isDefault) return -1;
        // _destroyされたものは後ろ
        if (a._destroy && !b._destroy) return 1;
        if (!a._destroy && b._destroy) return -1;
        // patternOrderで比較
        return (a.patternOrder || 0) - (b.patternOrder || 0);
      });
    },
    updatePatternOrders() {
      // アクティブなパターンのpatternOrderを再採番
      let order = 1;
      this.scenarioPopupPatterns.forEach(p => {
        if (!p._destroy && !p.isDefault) {
          p.patternOrder = order++;
        }
      });
    },
    getCollectionName(index) {
      return `設問 ${index + 1} 画像`;
    },
    clearErrorsForCollection(collectionId) {
      const index = this.collections.findIndex(c => c.id === collectionId);
      if (index !== -1) {
        this.$delete(this.collectionErrors, index);
      }
    },
    // default結果画像の保存
    saveDefaultPattern() {
      if (this.defaultResultType === 'iframe' && !this.iframe_enabled) {
        alert('結果設定を選択してください');
        return;
      }
      // iframe選択時
      if (this.defaultResultType === 'iframe') {
        const urlError = this.validateIframeUrl(this.defaultIframeEmbedUrl);
        if (urlError) {
          alert(urlError);
          return;
        }
        const existingDefaultIndex = this.scenarioPopupPatterns.findIndex(p => p.isDefault);
        const defaultObj = {
          id: existingDefaultIndex !== -1 ? this.scenarioPopupPatterns[existingDefaultIndex].id : null,
          patternOrder: 0,
          q1ChoiceId: null, q2ChoiceId: null, q3ChoiceId: null, q4ChoiceId: null, q5ChoiceId: null,
          imageData: null,
          image: null,
          iframeEmbedUrl: this.defaultIframeEmbedUrl,
          link: null, targetBlank: false, innerLink: false, noLink: false,
          withParameters: this.defaultIframeWithParameters, autoTransition: false,
          isDefault: true
        };
        this.defaultPatternObject = defaultObj;
        this.defaultPatternImage = null;
        if (existingDefaultIndex !== -1) {
          this.$set(this.scenarioPopupPatterns, existingDefaultIndex, defaultObj);
        } else {
          this.scenarioPopupPatterns.push(defaultObj);
        }
        this.updateScenarioField("scenario_popup_patterns", [...this.scenarioPopupPatterns]);
        this.showDefaultPatternForm = false;
        this.originalDefaultPatternImage = null;
        this.originalDefaultPatternImageObject = this.parseAndClone(defaultObj);
        this.originalDefaultIframeEmbedUrl = this.defaultIframeEmbedUrl;
        this.originalDefaultLinkSettings = { ...this.defaultLinkSettings };
        return;
      }

      // 画像ありの場合のみ画像チェック（新規アップロードがなく、既存画像もない場合はエラー）
      if (this.defaultHasImage && !this.tempImages.defaultPattern && (!this.defaultPatternObject || !this.defaultPatternObject.imageData)) {
        alert("画像が選択されていません。");
        return;
      }
      let linkSettings = {};
      if (this.$refs.defaultLinkSetting && this.$refs.defaultLinkSetting.getLinkSettings) {
        linkSettings = this.$refs.defaultLinkSetting.getLinkSettings();
      }
      // リンク設定のバリデーション
      const hasLinkType = linkSettings.target_blank || linkSettings.inner_link || linkSettings.no_link;
      if (!hasLinkType) {
        alert("リンクの選択をしてください。");
        return;
      }
      if (!linkSettings.no_link && !linkSettings.link) {
        alert("リンク先URLを入力してください。");
        return;
      }
      // 画像が新規に選択されている場合はそれを使用し、なければ既存の画像URLをそのまま利用
      const imageData = this.defaultHasImage
        ? (this.tempImages.defaultPatternPreview || (this.defaultPatternObject && this.defaultPatternObject.imageData))
        : null;
      const file = this.defaultHasImage
        ? (this.tempImages.defaultPattern || (this.defaultPatternObject && this.defaultPatternObject.image))
        : null;

      // scenarioPopupPatterns から既存のデフォルトパターンを探す
      const existingDefaultIndex = this.scenarioPopupPatterns.findIndex(p => p.isDefault);

      const defaultObj = {
        id: existingDefaultIndex !== -1 ? this.scenarioPopupPatterns[existingDefaultIndex].id : null,
        patternOrder: 0,
        q1ChoiceId: null,
        q2ChoiceId: null,
        q3ChoiceId: null,
        q4ChoiceId: null,
        q5ChoiceId: null,
        imageData: imageData,
        image: file,
        iframeEmbedUrl: null,
        link: linkSettings.link,
        targetBlank: linkSettings.target_blank,
        innerLink: linkSettings.inner_link,
        noLink: linkSettings.no_link,
        withParameters: linkSettings.with_parameters,
        autoTransition: linkSettings.auto_transition,
        isDefault: true
      };

      this.defaultLinkSettings = {
        targetBlank: linkSettings.target_blank,
        innerLink: linkSettings.inner_link,
        noLink: linkSettings.no_link,
        link: linkSettings.link,
        withParameters: linkSettings.with_parameters,
        autoTransition: linkSettings.auto_transition
      };
      this.defaultPatternObject = defaultObj;
      this.defaultPatternImage = imageData;

      // scenarioPopupPatterns に既存の default があれば上書き、なければ push
      if (existingDefaultIndex !== -1) {
        this.$set(this.scenarioPopupPatterns, existingDefaultIndex, defaultObj);
      } else {
        this.scenarioPopupPatterns.push(defaultObj);
      }
      this.updateScenarioField("scenario_popup_patterns", [...this.scenarioPopupPatterns]);

      // 状態リセット
      this.tempImages.defaultPattern = null;
      this.tempImages.defaultPatternPreview = '';
      this.showDefaultPatternForm = false;
      if (this.$refs.defaultPatternImageInput) this.$refs.defaultPatternImageInput.value = '';

      // 保存後の状態をoriginalとして保存（次回編集時のキャンセル用）
      this.originalDefaultPatternImage = this.defaultPatternImage;
      this.originalDefaultPatternImageObject = this.parseAndClone(defaultObj);
      this.originalDefaultIframeEmbedUrl = '';
      this.originalDefaultLinkSettings = { ...this.defaultLinkSettings };
    },
    // default結果画像のキャンセル（入力内容を元に戻す）
    cancelDefaultPattern() {
      // 一時ファイルをクリア
      this.tempImages.defaultPattern = null;
      this.tempImages.defaultPatternPreview = '';

      // defaultResultType と defaultIframeEmbedUrl を復元
      if (this.originalDefaultPatternImageObject) {
        if (this.originalDefaultPatternImageObject.iframeEmbedUrl) {
          this.defaultResultType = 'iframe';
          this.defaultIframeEmbedUrl = this.originalDefaultPatternImageObject.iframeEmbedUrl;
          this.defaultIframeWithParameters = this.originalDefaultPatternImageObject.withParameters || false;
        } else {
          this.defaultResultType = this.originalDefaultPatternImageObject.imageData ? 'image_yes' : 'image_none';
          this.defaultIframeEmbedUrl = '';
          this.defaultIframeWithParameters = false;
        }
      }

      // 初期状態に復元
      if (this.originalDefaultPatternImage) {
        this.defaultPatternImage = this.originalDefaultPatternImage;
      }
      if (this.originalDefaultPatternImageObject) {
        this.defaultPatternObject = this.parseAndClone(this.originalDefaultPatternImageObject);
      }
      if (this.originalDefaultLinkSettings) {
        this.defaultLinkSettings = { ...this.originalDefaultLinkSettings };

        // ResultImageLinkSettingコンポーネントの値もリセット
        if (this.$refs.defaultLinkSetting && this.$refs.defaultLinkSetting.resetSettings) {
          this.$refs.defaultLinkSetting.resetSettings({
            target_blank: this.defaultLinkSettings.targetBlank,
            inner_link: this.defaultLinkSettings.innerLink,
            no_link: this.defaultLinkSettings.noLink,
            link: this.defaultLinkSettings.link,
            with_parameters: this.defaultLinkSettings.withParameters,
            auto_transition: this.defaultLinkSettings.autoTransition
          });
        }
      }

      // scenarioPopupPatternsのデフォルトパターンも復元
      if (this.originalDefaultPatternImageObject) {
        const existingDefaultIndex = this.scenarioPopupPatterns.findIndex(p => p.isDefault);
        if (existingDefaultIndex !== -1) {
          this.$set(this.scenarioPopupPatterns, existingDefaultIndex, this.parseAndClone(this.originalDefaultPatternImageObject));
        }
        this.updateScenarioField("scenario_popup_patterns", [...this.scenarioPopupPatterns]);
      }

      // フォームを閉じる
      this.showDefaultPatternForm = false;
      if (this.$refs.defaultPatternImageInput) this.$refs.defaultPatternImageInput.value = '';
    },
    // default結果画像の編集：保存済みの値を入力フォームに反映
    editDefaultPattern() {
      if (this.defaultPatternObject) {
        if (this.defaultPatternObject.iframeEmbedUrl) {
          this.defaultResultType = 'iframe';
          this.defaultIframeEmbedUrl = this.defaultPatternObject.iframeEmbedUrl;
          this.defaultIframeWithParameters = this.defaultPatternObject.withParameters || false;
        } else {
          this.defaultResultType = this.defaultPatternObject.imageData ? 'image_yes' : 'image_none';
          this.defaultIframeEmbedUrl = '';
          this.defaultIframeWithParameters = false;
        }
      }
      this.showDefaultPatternForm = true
    },
    openAnimationModal(collectionId, choiceId) {
      this.currentCollectionId = collectionId;
      this.currentChoiceId = choiceId;
      this.tempImages.animation = null;
      this.animationImagePreview = null;

      const previewList = this.$store.state.creative.preview_list;
      const collection = previewList?.collections?.find(c => c.id === collectionId || c.temp_id === collectionId);
      const choice = collection?.choices?.find(ch => ch.id === choiceId || ch.temp_id === choiceId);

      if (choice?.animationImagePreview) {
        this.tempImages.animation = choice.animationImage;
        this.animationImagePreview = choice.animationImagePreview;
      }

      this.$bvModal.show('animation-image-modal');
    },
    // モーダルを閉じる
    resetAnimationModal() {
      this.currentCollectionId = null;
      this.currentChoiceId = null;
      this.tempImages.animation = null;
      this.animationImagePreview = null;
      if (this.$refs.animationImageInput) this.$refs.animationImageInput.value = '';
    },
    // input fileでファイルが選択されたときの処理
    handleAnimationImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      // 一時保存用にファイルオブジェクトをセット
      this.tempImages.animation = file;
      // プレビュー用URLを生成
      this.animationImagePreview = URL.createObjectURL(file);
    },
    // 保存ボタンで実際に対象のchoiceにアニメーション画像を保存
    // 保存ボタン押下時の処理
    saveAnimationImage() {
      if (!this.tempImages.animation) {
        alert("ファイルが選択されていません。");
        return;
      }
      this.$store.commit('creative/updateChoiceAnimation', {
        collectionId: this.currentCollectionId,
        choiceId: this.currentChoiceId,
        animationImage: this.tempImages.animation,
        animationImagePreview: this.animationImagePreview
      });
      // ローカルの choice も直接更新
      const localChoice = this.findLocalChoice(this.currentCollectionId, this.currentChoiceId);
      if (localChoice) {
        this.$set(localChoice, 'animationImage', this.tempImages.animation);
        this.$set(localChoice, 'animationImagePreview', this.animationImagePreview);
      }
      // モーダルを閉じる
      this.$bvModal.hide('animation-image-modal');
    },
    deleteAnimationImage(collectionId, choiceId) {
      if (confirm('アニメーション画像を削除してもよろしいですか？')) {
        this.$store.commit('creative/deleteChoiceAnimation', {
          collectionId: collectionId,
          choiceId: choiceId
        });
        // ローカルの choice も直接更新
        const localChoice = this.findLocalChoice(collectionId, choiceId);
        if (localChoice) {
          this.$set(localChoice, 'animationImage', null);
          this.$set(localChoice, 'animationImagePreview', null);
        }
      }
    },
    deleteAnimationImageFromModal() {
      if (confirm('アニメーション画像を削除してもよろしいですか？')) {
        this.$store.commit('creative/deleteChoiceAnimation', {
          collectionId: this.currentCollectionId,
          choiceId: this.currentChoiceId
        });
        // ローカルの choice も直接更新
        const localChoice = this.findLocalChoice(this.currentCollectionId, this.currentChoiceId);
        if (localChoice) {
          this.$set(localChoice, 'animationImage', null);
          this.$set(localChoice, 'animationImagePreview', null);
        }
        this.$bvModal.hide('animation-image-modal');
      }
    },
    deleteLoadingImage() {
      this.loadingImage = null;
      this.updateScenarioField("loading_image", null);
      this.$store.commit("creative/setDeleteLoadingImage", true);
    },
    // テーブル表示用メソッド
    getPatternLabel(index) {
      return `#${index + 1}`;
    },
    getPatternChoiceForTable(pattern, collection) {
      const choiceId = this.getPatternChoiceIdForTable(pattern, collection);

      // 0 は * (指定なし)
      if (choiceId === 0) return '*';
      // null は - (なし)
      if (choiceId === null) return '-';

      const choice = collection.choices.find(c =>
        (c.id || c.temp_id) == choiceId && !c._destroy
      );
      if (!choice) return '*';

      // "ボタン1" から数字だけ抽出
      const match = choice.buttonName.match(/\d+/);
      return match ? match[0] : choice.buttonName;
    },
    getPatternChoiceIdForTable(pattern, collection) {
      // activeCollections内での設問のインデックスを取得
      const collectionIndex = this.activeCollections.findIndex(
        c => (c.id || c.temp_id) === (collection.id || collection.temp_id)
      );
      
      if (collectionIndex === -1) return null;
      
      // 新しい横持ちデータ構造（q1ChoiceId〜q5ChoiceId）を優先的に使用
      const qKeysCamel = ['q1ChoiceId', 'q2ChoiceId', 'q3ChoiceId', 'q4ChoiceId', 'q5ChoiceId'];
      const qKeysSnake = ['q1_choice_id', 'q2_choice_id', 'q3_choice_id', 'q4_choice_id', 'q5_choice_id'];
      
      // キャメルケースをチェック（キーが存在するかどうか）
      if (qKeysCamel[collectionIndex] in pattern) {
        return pattern[qKeysCamel[collectionIndex]];
      }
      // スネークケースをチェック（キーが存在するかどうか）
      if (qKeysSnake[collectionIndex] in pattern) {
        return pattern[qKeysSnake[collectionIndex]];
      }
      
      // 旧データ構造（scenarioImageId + choiceId）をフォールバック
      if ((collection.id || collection.temp_id) === pattern.scenarioImageId) {
        return pattern.choiceId;
      }
      return null;
    },
    calculateCoverageForPattern(pattern) {
      // パターンがカバーする通り数を計算
      // '*' (0) のみカバレッジに含める、'-' (null) や具体的なボタンは1通り
      let coverage = 1;
      this.activeCollections.forEach(collection => {
        const choiceDisplay = this.getPatternChoiceForTable(pattern, collection);
        if (choiceDisplay === '*') {
          // ワイルドカード（指定なし）の場合、その設問のボタン数分を掛ける
          const choiceCount = collection.choices.filter(c => !c._destroy).length;
          coverage *= choiceCount || 1;
        }
        // '-' (なし) や具体的なボタンの場合は1通り
      });
      return coverage;
    },
    // パターンエディター関連メソッド
    openPatternEditor(pattern, index) {
      this.editingPattern = pattern;
      this.editingPatternIndex = index;
      this.showPatternEditor = true;
      
      // 選択肢を初期化
      // DB値: 0 -> * (空文字), null -> なし ('none'), その他 -> choice_id
      this.patternEditorChoices = {};
      this.activeCollections.forEach((collection, qIdx) => {
        if (pattern) {
          const choiceId = this.getPatternChoiceIdForTable(pattern, collection);
          if (choiceId === 0) {
            this.patternEditorChoices[qIdx] = '';  // * (指定なし)
          } else if (choiceId === null) {
            this.patternEditorChoices[qIdx] = 'none';  // なし
          } else {
            this.patternEditorChoices[qIdx] = choiceId;
          }
        } else {
          this.patternEditorChoices[qIdx] = '';
        }
      });
      
      // 画像情報を初期化
      if (pattern && pattern.nextScenarioPopupId) {
        this.patternEditorResultType = 'next_popup';
        this.patternEditorNextScenarioPopupId = pattern.nextScenarioPopupId;
      } else if (pattern && pattern.imageData) {
        this.patternEditorResultType = 'image_yes';
        this.patternEditorNextScenarioPopupId = null;
      } else if (pattern && pattern.iframeEmbedUrl) {
          this.patternEditorResultType = 'iframe';
          this.patternEditorNextScenarioPopupId = null;
      } else {
        this.patternEditorResultType = pattern ? 'image_none' : 'image_yes';
        this.patternEditorNextScenarioPopupId = null;
      }
      this.patternEditorImagePreview = pattern ? pattern.imageData : null;
      this.tempImages.patternEditor = null;

      this.patternEditorLinkSettings = {
        targetBlank: pattern?.targetBlank || false,
        innerLink: pattern?.innerLink || false,
        noLink: pattern?.noLink || false,
        link: pattern?.link || '',
        withParameters: pattern?.withParameters || false,
        autoTransition: pattern?.autoTransition || false
      };
      this.patternEditorIframeEmbedUrl = pattern?.iframeEmbedUrl || '';
      // iframe用のパラメータ引継ぎは、実際にiframeパターンだった場合のみ保存済みの値を引き継ぐ（リンク設定側の値と混ざらないようにする）
      this.patternEditorIframeWithParameters = pattern?.iframeEmbedUrl ? (pattern?.withParameters || false) : false;
    },
    closePatternEditor() {
      this.showPatternEditor = false;
      this.editingPattern = null;
      this.editingPatternIndex = -1;
      if (this.$refs.patternImageInput) this.$refs.patternImageInput.value = '';
      this.patternEditorIframeEmbedUrl = '';
      this.patternEditorIframeWithParameters = false;
    },
    onPatternChoiceChange(qIdx) {
      // 「なし」を選んだら、それ以降の設問を全て「なし」にする
      if (this.patternEditorChoices[qIdx] === 'none') {
        for (let i = qIdx + 1; i < this.activeCollections.length; i++) {
          this.$set(this.patternEditorChoices, i, 'none');
        }
      }
      this.$forceUpdate();
    },
    isPatternChoiceDisabled(qIdx) {
      // 未登録の設問枠は非活性
      if (!this.activeCollections[qIdx]) return true;
      // 前の設問で「なし」が選択されていたら、この設問は非活性
      for (let i = 0; i < qIdx; i++) {
        if (this.patternEditorChoices[i] === 'none') {
          return true;
        }
      }
      return false;
    },
    getPatternEditorChoiceLabel(qIdx) {
      const choiceId = this.patternEditorChoices[qIdx];
      if (!choiceId) return '条件に含めない(*)';
      if (choiceId === 'none') return 'なし';

      const collection = this.activeCollections[qIdx];
      if (!collection) return '条件に含めない(*)';

      const choice = collection.choices.find(c =>
        (c.id || c.temp_id) == choiceId && !c._destroy
      );
      return choice ? choice.buttonName : '条件に含めない(*)';
    },
    calculatePatternEditorCoverage() {
      let coverage = 1;
      this.activeCollections.forEach((collection, qIdx) => {
        const choiceId = this.patternEditorChoices[qIdx];
        // 空文字（*）の場合のみカバレッジ計算に含める
        // 'none'（なし）や具体的なchoice_idの場合は1通り
        if (choiceId === '' || choiceId === undefined) {
          const choiceCount = collection.choices.filter(c => !c._destroy).length;
          coverage *= choiceCount || 1;
        }
        // 'none' の場合はそれ以降のcoverageには影響しない（1通り）
      });
      return coverage;
    },
    handlePatternResultImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      this.tempImages.patternEditor = file;
      this.patternEditorImagePreview = URL.createObjectURL(file);
    },
    savePattern() {
      // バリデーション
      if (this.patternEditorResultType === 'image_yes' && !this.patternEditorImagePreview) {
        alert('結果画像を選択してください');
        return;
      }

      if (this.patternEditorResultType === 'next_popup' && !this.patternEditorNextScenarioPopupId) {
        alert('遷移先のシナリオポップアップを選択してください');
        return;
      }

      if (this.patternEditorResultType === 'iframe') {
        const urlError = this.validateIframeUrl(this.patternEditorIframeEmbedUrl);
        if (urlError) {
          alert(urlError);
          return;
        }
      }

      if (this.patternEditorResultType === 'iframe' && !this.iframe_enabled) {
        alert('結果設定を選択してください');
        return;
      }

      let linkSettings = { target_blank: false, inner_link: false, no_link: true, link: '', with_parameters: false, auto_transition: false };
      if (this.patternEditorResultType !== 'next_popup' && this.patternEditorResultType !== 'iframe') {
        if (this.$refs.patternLinkSetting) {
          linkSettings = this.$refs.patternLinkSetting.getLinkSettings ?
            this.$refs.patternLinkSetting.getLinkSettings() :
            {
              target_blank: this.patternEditorLinkSettings.targetBlank,
              inner_link: this.patternEditorLinkSettings.innerLink,
              no_link: this.patternEditorLinkSettings.noLink,
              link: this.patternEditorLinkSettings.link,
              with_parameters: this.patternEditorLinkSettings.withParameters,
              auto_transition: this.patternEditorLinkSettings.autoTransition
            };
        }
        const hasLinkType = linkSettings.target_blank || linkSettings.inner_link || linkSettings.no_link;
        if (!hasLinkType) {
          alert("リンクの選択をしてください。");
          return;
        }
        if (!linkSettings.no_link && !linkSettings.link) {
          alert("リンク先URLを入力してください。");
          return;
        }
      }

      // 各設問ごとのchoice_idを取得
      // *: 0, なし: null, ボタン選択: choice_id
      const convertChoiceId = (val) => {
        if (val === '' || val === 0 || val === '0') return 0;  // * は 0
        if (val === 'none') return null;   // なし は null
        return val;
      };
      const q1ChoiceId = convertChoiceId(this.patternEditorChoices[0]);
      const q2ChoiceId = convertChoiceId(this.patternEditorChoices[1]);
      const q3ChoiceId = convertChoiceId(this.patternEditorChoices[2]);
      const q4ChoiceId = convertChoiceId(this.patternEditorChoices[3]);
      const q5ChoiceId = convertChoiceId(this.patternEditorChoices[4]);
      if (this.editingPattern) {
        // 既存パターンの更新
        this.editingPattern.q1ChoiceId = q1ChoiceId;
        this.editingPattern.q2ChoiceId = q2ChoiceId;
        this.editingPattern.q3ChoiceId = q3ChoiceId;
        this.editingPattern.q4ChoiceId = q4ChoiceId;
        this.editingPattern.q5ChoiceId = q5ChoiceId;
        if (this.patternEditorHasImage) {
          // 画像ありの場合：新規アップロードがあれば更新、なければ既存を維持
          if (this.tempImages.patternEditor) {
            this.editingPattern.imageData = this.patternEditorImagePreview;
            this.editingPattern.image = this.tempImages.patternEditor;
          }
        } else {
          // 画像なしの場合
          this.editingPattern.imageData = null;
          this.editingPattern.image = null;
        }
        this.editingPattern.link = linkSettings.link;
        this.editingPattern.targetBlank = linkSettings.target_blank;
        this.editingPattern.innerLink = linkSettings.inner_link;
        this.editingPattern.noLink = linkSettings.no_link;
        this.editingPattern.withParameters = this.patternEditorResultType === 'iframe' ? this.patternEditorIframeWithParameters : linkSettings.with_parameters;
        this.editingPattern.autoTransition = linkSettings.auto_transition;
        if (this.patternEditorResultType === 'iframe') {
          this.editingPattern.iframeEmbedUrl = this.patternEditorIframeEmbedUrl;
          this.editingPattern.imageData = null;
          this.editingPattern.image = null;
          this.editingPattern.nextScenarioPopupId = null;
        } else {
          this.editingPattern.iframeEmbedUrl = null;
        }
        if (this.patternEditorResultType === 'next_popup') {
          this.editingPattern.imageData = null;
          this.editingPattern.image = null;
          this.editingPattern.link = '';
          this.editingPattern.noLink = true;
          this.editingPattern.nextScenarioPopupId = this.patternEditorNextScenarioPopupId;
        } else {
          this.editingPattern.nextScenarioPopupId = null;
        }
      } else {
        // 新規パターンの追加
        const nonDefaultPatterns = this.scenarioPopupPatterns.filter(p => !p._destroy && !p.isDefault);
        const isNextPopup = this.patternEditorResultType === 'next_popup';
        const newPattern = {
          id: null,
          patternOrder: nonDefaultPatterns.length + 1,
          q1ChoiceId: q1ChoiceId,
          q2ChoiceId: q2ChoiceId,
          q3ChoiceId: q3ChoiceId,
          q4ChoiceId: q4ChoiceId,
          q5ChoiceId: q5ChoiceId,
          imageData: this.patternEditorResultType === 'image_yes' ? this.patternEditorImagePreview : null,
          image: this.patternEditorResultType === 'image_yes' ? this.tempImages.patternEditor : null,
          link: isNextPopup ? '' : linkSettings.link,
          targetBlank: linkSettings.target_blank,
          innerLink: linkSettings.inner_link,
          noLink: isNextPopup ? true : linkSettings.no_link,
          withParameters: this.patternEditorResultType === 'iframe' ? this.patternEditorIframeWithParameters : linkSettings.with_parameters,
          autoTransition: linkSettings.auto_transition,
          isDefault: false,
          nextScenarioPopupId: isNextPopup ? this.patternEditorNextScenarioPopupId : null,
          iframeEmbedUrl: this.patternEditorResultType === 'iframe' ? this.patternEditorIframeEmbedUrl : null,
        };
        this.scenarioPopupPatterns.push(newPattern);
      }
      
      // 配列のコピーを渡してVuexストアの直接変更を防ぐ
      this.updateScenarioField("scenario_popup_patterns", [...this.scenarioPopupPatterns]);
      this.closePatternEditor();
    },
    // 結果画像・デフォルト結果画像を初期化（設問画像が全て削除された場合）
    resetPatternsAndDefault() {
      // パターンを削除（DB保存済みは_destroyフラグ、未保存は配列から削除）
      for (let i = this.scenarioPopupPatterns.length - 1; i >= 0; i--) {
        const pattern = this.scenarioPopupPatterns[i];
        if (pattern.id) {
          this.$set(pattern, '_destroy', true);
        } else {
          this.scenarioPopupPatterns.splice(i, 1);
        }
      }
      this.updateScenarioField('scenario_popup_patterns', [...this.scenarioPopupPatterns]);

      // デフォルト結果画像を初期化
      this.defaultPatternImage = null;
      this.defaultPatternObject = null;
      this.defaultPatternError = false;
      this.defaultHasImage = true;
      this.showDefaultPatternForm = false;
      this.tempImages.defaultPattern = null;
      this.tempImages.defaultPatternPreview = '';
      this.originalDefaultPatternImage = null;
      this.originalDefaultPatternImageObject = null;
      this.originalDefaultLinkSettings = null;
      this.defaultLinkSettings = {
        targetBlank: false,
        innerLink: false,
        noLink: false,
        link: '',
        withParameters: false,
        autoTransition: false
      };

      // パターンエディターも閉じる
      this.showPatternEditor = false;
      this.editingPattern = null;
      this.editingPatternIndex = -1;
    },
    linkTabLabel,
    getNextPopupInfo(pattern) {
      return this.chainedPopupOptions.find(p => p.id === pattern.nextScenarioPopupId) || {
        name: pattern.nextScenarioPopupName,
        thumbnail_url: pattern.nextScenarioPopupThumbnailUrl
      };
    },
    async preloadChainedPopupOptions() {
      if (this.chainedPopupOptions.length > 0) return
      this.popupOptionsLoading = true
      try {
        const res = await CreativeApi.chained_popup_options({ popup_url_token: this.scenario_popup_url_token })
        this.chainedPopupOptions = res.data
      } finally {
        this.popupOptionsLoading = false
      }
    },
    async openPopupSelectionModal() {
      this.showPopupSelectionModal = true
      await this.preloadChainedPopupOptions()
    },
    onPopupSelected(id) {
      this.patternEditorNextScenarioPopupId = id
      this.showPopupSelectionModal = false
    },
    onPopupCleared() {
      this.patternEditorNextScenarioPopupId = null
      this.showPopupSelectionModal = false
    },
    validateIframeUrl(url) {
      if (!url) return 'iframe URLを入力してください';
      if (url.length > 255) return 'iframe URLは255文字以内で入力してください';
      try { new URL(url); } catch (e) { return 'URL形式が正しくありません'; }
      if (!/^https:\/\//i.test(url)) return 'https のURLを入力してください';
      return null;
    },
  }
};
</script>

<style scoped>
.scenario-popup-editor {
  padding-bottom: 96px;
}
.image-container {
  user-select: none;
  margin-top: 1rem;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  width: 100%;
  max-width: 560px;
  min-height: 180px;
  background: #f0f2f5;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
}
.image-container .editor-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: 420px;
  min-height: 160px;
  object-fit: contain;
  background: #f0f2f5;
  vertical-align: top;
}
.image-container.no-more-choices {
  cursor: not-allowed;
}
.selected-area, .drawing-area {
  position: absolute;
  border: 2px solid #007bff;
  background-color: rgba(0, 123, 255, 0.2);
  pointer-events: none;
}
.selected-area {
  pointer-events: auto;
}
.choice-number {
  position: absolute;
  top: -25px;
  left: -2px;
  background-color: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.question-grid {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}
.question-card {
  width: 180px;
  flex: 0 0 180px;
  min-width: 0;
  max-width: 180px;
  box-sizing: border-box;
  border: 1px solid #dfe5ec;
  border-radius: 9px;
  overflow: visible;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.question-card > .q-title,
.question-card > .image-wrapper,
.question-card > .q-meta,
.question-card > .q-actions {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}
.image-wrapper {
  position: relative;
  width: 100%;
  height: 100px;
  overflow: hidden;
  background: #e8f5ff;
  border-bottom: 1px solid #dfe5ec;
}
.image-wrapper img.card-img-top {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.question-card .q-title {
  background: #007bff;
  color: #fff;
  font-weight: 700;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  flex-shrink: 0;
  border-radius: 8px 8px 0 0;
}
.question-card .q-meta {
  padding: 8px 10px 4px;
  font-size: 12px;
  line-height: 1.5;
  flex-shrink: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.question-card .q-actions {
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 10px;
  flex-shrink: 0;
  position: relative;
  overflow: visible;
  border-radius: 0 0 8px 8px;
}
.question-card .q-actions .btn {
  padding: 0.15rem 0.45rem;
  font-size: 0.75rem;
  line-height: 1.3;
}
.loading-image-block {
  margin-top: 16px;
  margin-bottom: 8px;
  padding: 14px;
  border: 1.5px dashed #c9d3df;
  border-radius: 9px;
  background: #f8fafc;
}
.loading-row {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.loading-thumb {
  flex: 0 0 auto;
  max-width: 180px;
  max-height: 96px;
  border: 1px solid #dfe5ec;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  line-height: 0;
}
.loading-thumb img {
  display: block;
  width: auto;
  height: auto;
  max-width: 180px;
  max-height: 96px;
  object-fit: contain;
}
.loading-thumb--empty {
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.3;
}
.loading-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.drag-drop-instruction {
  background-color: #e3f2fd;
  border-color: #90caf9;
  color: #0d47a1;
}
.drag-drop-instruction i {
  font-size: 1.5rem;
}
.image-preview {
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 0.5rem;
  background-color: #f8f9fa;
}
.image-preview img {
  max-height: 200px;
  width: 100%;
  object-fit: contain;
}
.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
.bi {
  font-size: 1rem;
}
.error-message {
  background-color: #f8d7da;
  padding: 0.5rem;
  border-radius: 4px;
}

/* 結果画像登録セクション全体を左寄せ */
.scenario-result-section {
  text-align: left;
  margin-left: 0;
  margin-right: 0;
}

.default-pattern-preview-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.default-pattern-preview-media {
  flex: 0 0 auto;
}

.default-pattern-preview-media img {
  display: block;
  max-height: 150px;
  max-width: 220px;
  width: auto;
  height: auto;
  border-radius: 6px;
}

.default-pattern-preview-meta {
  flex: 0 1 auto;
  min-width: 0;
}

/* 回答シナリオテーブル用スタイル */
.scenario-list-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
}

.scenario-list-side {
  flex: 0 1 220px;
  min-width: 180px;
  padding-top: 4px;
}

.scenario-wrap {
  flex: 0 1 auto;
  overflow-x: auto;
  max-width: 100%;
}

.scenario-table {
  font-size: 0.85rem;
  width: max-content;
  max-width: 100%;
  table-layout: fixed;
  min-width: 420px;
  border-collapse: collapse;
  margin: 0;
}

.scenario-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  vertical-align: middle;
  white-space: nowrap;
  padding: 9px 8px;
}

.scenario-table td {
  vertical-align: middle;
  padding: 9px 8px;
}

.scenario-table .col-priority {
  width: 62px;
}
.scenario-table .col-question {
  width: 84px;
}
.scenario-table .col-result {
  width: 120px;
}
.scenario-table .col-actions {
  width: 88px;
}

/* 未登録の設問枠（最大5のうち未使用分） */
.scenario-table th.question-slot-empty,
.scenario-table td.question-slot-empty {
  background-color: #e9ecef;
  color: #adb5bd;
}

.pattern-select-column.question-slot-empty {
  opacity: 0.55;
}

.pattern-select-column.question-slot-empty .pattern-question-label {
  color: #adb5bd;
}

.pattern-select-column.question-slot-empty select:disabled {
  background-color: #e9ecef;
  color: #adb5bd;
}

.order-controls {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
}
.priority-label {
  display: block;
  margin-bottom: 2px;
}

.result-text {
  max-width: 110px;
  margin: 0 auto;
}

.scenario-actions {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
}

.btn-icon {
  width: 34px;
  height: 34px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

/* グローバル .btn .fas { margin-right: 5px } によるズレを打ち消す */
.btn-icon .fas,
.btn-icon .fab {
  margin: 0;
  line-height: 1;
  display: block;
}

.btn-group-vertical .btn {
  margin-bottom: 2px;
}

/* 優先順位ボタン */
.priority-btn {
  width: 28px;
  height: 23px;
  padding: 0;
}

.priority-btn i {
  display: block;
  width: 100%;
  text-align: center;
  line-height: 23px;
}

/* テーブル内の結果画像（固定サイズで設問列の伸縮と切り離す） */
.result-table-image {
  width: 72px;
  height: 54px;
  max-width: 72px;
  max-height: 54px;
  object-fit: cover;
  border-radius: 5px;
  border: 1px solid #dfe5ec;
}

/* パターンエディターの列 */
.pattern-select-column {
  height: 38px;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.pattern-select-column select {
  max-width: 150px;
}

.pattern-question-label {
  font-size: 0.9rem;
  color: #495057;
  white-space: nowrap;
  margin-right: 0.5rem;
}

/* 編集中のボタン無効化スタイル */
.card-body button:disabled {
  background-color: #cccccc !important;
  border-color: #cccccc !important;
  color: #666666 !important;
  opacity: 1 !important;
}

/* カスタムツールチップ（画面上に重ねて浮かせ、レイアウト幅は変えない） */
.custom-tooltip {
  position: relative;
  display: inline-block;
}

.custom-tooltip[data-tooltip]::before {
  content: attr(data-tooltip);
  visibility: hidden;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  position: fixed;
  z-index: 2000;
  left: calc(var(--tt-left, 0px) + var(--tt-width, 0px) * 0.5);
  top: var(--tt-top, 0px);
  transform: translate(8px, calc(-100% - 8px));
  padding: 8px 12px;
  background: linear-gradient(135deg, #343a40 0%, #495057 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.custom-tooltip[data-tooltip]:hover::before {
  visibility: visible;
  opacity: 1;
}

.custom-tooltip[data-tooltip=""]::before {
  display: none;
}

.btn-group .custom-tooltip,
.btn-group-vertical .custom-tooltip {
  display: inline-flex;
}

.custom-tooltip.tooltip-top-right[data-tooltip]::before {
  left: calc(var(--tt-left, 0px) + var(--tt-width, 0px) * 0.5);
  top: var(--tt-top, 0px);
  transform: translate(8px, calc(-100% - 8px));
}

.custom-tooltip.tooltip-top-center[data-tooltip]::before {
  left: calc(var(--tt-left, 0px) + var(--tt-width, 0px) * 0.5);
  top: var(--tt-top, 0px);
  transform: translate(-25%, calc(-100% - 8px));
  white-space: nowrap;
}

.custom-tooltip.tooltip-top-left[data-tooltip]::before {
  left: calc(var(--tt-left, 0px) + var(--tt-width, 0px));
  top: var(--tt-top, 0px);
  transform: translate(-100%, calc(-100% - 8px));
  white-space: nowrap;
}

.custom-tooltip.tooltip-left[data-tooltip]::before {
  left: var(--tt-left, 0px);
  top: calc(var(--tt-top, 0px) + var(--tt-height, 0px) * 0.5);
  transform: translate(calc(-100% - 8px), -50%);
  white-space: nowrap;
}

.scenario-editor-footer-spacer {
  height: 16px;
}

.scenario-editor-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1030;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 12px 22px;
  background: rgba(255, 255, 255, 0.96);
  border-top: 1px solid #dfe5ec;
  backdrop-filter: blur(8px);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.06);
}

.scenario-editor-footer-message {
  margin-right: auto;
}

@media (min-width: 992px) {
  .sidebar-mini.sidebar-collapse .scenario-editor-footer {
    left: 4.6rem;
  }
  body:not(.sidebar-collapse) .scenario-editor-footer {
    left: 250px;
  }
}
</style>
